'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchImageLimits, readImage, type ImageLimits, type ImageText } from '@/lib/api';
import Layer from './Layer';

/**
 * Turn the writing in a picture into text in the box.
 *
 * The sibling of VoiceDialog, and it looks like one on purpose — the same drop
 * zone, the same limits stated before anything is chosen, the same "put it in
 * the box" at the end. Somebody who has used one should not have to learn the
 * other.
 *
 * ── Where it differs, and why ────────────────────────────────────────────────
 *
 * A transcription is a job: it takes half a minute, it arrives in pieces, and
 * the dialog follows it. Reading a picture is one request of about a second and
 * a half, so there is nothing to follow and nothing to cancel worth building a
 * job for. What the wait actually consists of is the UPLOAD — a photo off a
 * phone is a few megabytes over a phone connection — which is why the bar
 * measures that honestly and then keeps moving while the server reads.
 *
 * The other difference is the preview. A voice file cannot be checked by
 * looking at it; a photograph can, and the commonest failure of this feature is
 * a picture where the writing is too small or half out of frame. Showing it
 * back at the moment of choosing turns that into something the user fixes in
 * five seconds, instead of a wrong answer they have to work out the cause of.
 */

type Phase = 'idle' | 'uploading' | 'reading' | 'done' | 'error';

function prettyBytes(n: number): string {
  return n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} kB`;
}

export default function ImageDialog({
  onClose,
  onText,
}: {
  onClose: () => void;
  /** Called once, with the words off the picture, when the user takes them. */
  onText: (text: string) => void;
}) {
  const [limits, setLimits] = useState<ImageLimits | null>(null);
  const [file, setFile] = useState<Blob | null>(null);
  const [name, setName] = useState<string>('');
  const [preview, setPreview] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [uploadPct, setUploadPct] = useState(0);
  const [creep, setCreep] = useState(0);
  const [result, setResult] = useState<ImageText | null>(null);
  const [error, setError] = useState<string | null>(null);

  const panel = useRef<HTMLDivElement>(null);
  const abort = useRef<AbortController | null>(null);
  /* Drag events fire on every child element, so a plain boolean flickers the
     highlight off the moment the pointer crosses the text inside the zone.
     Counting enter/leave pairs is the standard fix — the same one VoiceDialog
     uses. */
  const dragDepth = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    fetchImageLimits(controller.signal).then(setLimits).catch(() => setLimits(null));
    return () => controller.abort();
  }, []);

  // Escape closes, and the panel takes focus so a screen reader lands inside it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    panel.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // An upload in flight when this unmounts must be aborted, or the browser
  // holds the connection open for a dialog nobody is looking at any more.
  useEffect(() => () => { abort.current?.abort(); }, []);

  // The preview is an object URL, and object URLs leak until they are revoked.
  // One per chosen picture, released when the next one arrives or the dialog goes.
  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pick = useCallback((chosen: Blob | null | undefined, label?: string) => {
    if (!chosen) return;
    setError(null);
    if (chosen.type && !chosen.type.startsWith('image/')) {
      setError('That is not an image. A photo or a screenshot is what this reads.');
      return;
    }
    if (limits && chosen.size > limits.maxBytes) {
      setError(`That image is ${prettyBytes(chosen.size)} — the limit is ${prettyBytes(limits.maxBytes)}.`);
      return;
    }
    setFile(chosen);
    setName(label ?? (chosen as File).name ?? 'pasted image');
    setPhase('idle');
    setResult(null);
  }, [limits]);

  /*
   * Ctrl+V, because half the pictures anybody wants read are already in the
   * clipboard rather than on the disk: a screenshot just taken, an image copied
   * out of a chat window or a PDF viewer. Saving that to a file first only to
   * pick it back up is a step with nothing in it.
   *
   * On `document` rather than on the panel, because the panel holds no text
   * input for the paste to land in — with a listener on a focused element the
   * event would never arrive.
   */
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (phase === 'uploading' || phase === 'reading') return;
      const item = Array.from(e.clipboardData?.items ?? []).find((i) => i.type.startsWith('image/'));
      const blob = item?.getAsFile();
      if (blob) {
        e.preventDefault();
        pick(blob, 'pasted image');
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [pick, phase]);

  /*
   * The bar after the upload finishes.
   *
   * The server does not report progress while it reads — there is nothing to
   * report, it is one call — so this is a creep towards 92% that never arrives:
   * each tick closes an eighth of the remaining gap. It is not pretending to
   * know anything. It is the difference between a wait that looks like work and
   * one that looks like a hang, over the second and a half it actually takes.
   */
  useEffect(() => {
    if (phase !== 'reading') return;
    const timer = setInterval(() => setCreep((p) => p + (92 - p) * 0.13), 220);
    return () => clearInterval(timer);
  }, [phase]);

  function close() {
    abort.current?.abort();
    onClose();
  }

  async function parse() {
    if (!file) return;
    setError(null);
    setResult(null);
    setPhase('uploading');
    setUploadPct(0);
    setCreep(0);
    abort.current = new AbortController();

    try {
      const text = await readImage(file, (fraction) => {
        setUploadPct(fraction);
        // The upload finishing is what starts the reading — no separate signal
        // comes back, and waiting for one would leave the bar parked at 45%.
        if (fraction >= 1) { setCreep(45); setPhase('reading'); }
      }, abort.current.signal);
      setResult(text);
      setPhase('done');
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      setError((err as Error).message || 'That image could not be read.');
      setPhase('error');
    }
  }

  const busy = phase === 'uploading' || phase === 'reading';
  /** The upload is the first 45%; the creep above owns the rest. */
  const pct = phase === 'uploading'
    ? Math.round(uploadPct * 45)
    : phase === 'reading'
      ? Math.round(creep)
      : phase === 'done' ? 100 : 0;
  const text = result?.text ?? '';

  return (
    <Layer>
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) close(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Read the text in an image"
    >
      <div
        ref={panel}
        tabIndex={-1}
        className="flex max-h-[88vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-ink-200
                   bg-white shadow-2xl outline-none dark:border-ink-700 dark:bg-ink-900"
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-ink-200 px-5 py-4 dark:border-ink-800">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-200/60
                           text-accent-700 dark:bg-accent-700/35 dark:text-accent-300">
            {/* The same picture glyph the button in the toolbar carries, so the
                button and what it opens agree — the lesson VoiceDialog's icon
                learned when it had a microphone on both. */}
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"
                 strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
              <circle cx="8.5" cy="10" r="1.6" />
              <path d="M3.5 16.5l4.7-4.2a2 2 0 012.7 0l3.4 3.1a2 2 0 002.6.05l1.6-1.3 2 1.8" />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-ink-900 dark:text-ink-50">Image to text</h2>
            <p className="truncate text-[11px] text-ink-500 dark:text-ink-400">
              A sign, a menu, a page, a screenshot — the words come out in the language they are written in.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="rounded-full p-2 text-ink-400 transition hover:bg-ink-100 hover:text-ink-700
                       dark:hover:bg-ink-800 dark:hover:text-ink-100"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9">
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {limits && !limits.enabled ? (
            <p className="text-sm text-ink-600 dark:text-ink-300">
              Reading images is switched off at the moment.
            </p>
          ) : (
            <>
              {/* The drop zone doubles as the file button: the whole thing is a
                  label, so a click anywhere in it opens the picker — and on a
                  phone that picker offers the camera, which is the case this
                  feature exists for. */}
              <label
                onDragEnter={(e) => { e.preventDefault(); dragDepth.current++; setDragging(true); }}
                onDragOver={(e) => e.preventDefault()}
                onDragLeave={(e) => {
                  e.preventDefault();
                  dragDepth.current = Math.max(0, dragDepth.current - 1);
                  if (!dragDepth.current) setDragging(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  dragDepth.current = 0;
                  setDragging(false);
                  if (!busy) pick(e.dataTransfer.files?.[0]);
                }}
                className={
                  'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed ' +
                  'px-6 py-6 text-center transition ' +
                  (dragging
                    ? 'border-accent-500 bg-accent-100/70 dark:border-accent-400 dark:bg-accent-700/25'
                    : 'border-ink-200 hover:border-ink-300 hover:bg-ink-100/60 dark:border-ink-700 dark:hover:bg-ink-800/60') +
                  (busy ? ' pointer-events-none opacity-60' : '')
                }
              >
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => pick(e.target.files?.[0])}
                />
                {/* The picture itself, once there is one. This is the check that
                    catches the blurred, the cropped and the sideways before the
                    server is asked to make sense of them. */}
                {preview ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={preview}
                    alt=""
                    className="max-h-44 w-auto rounded-lg border border-ink-200 object-contain shadow-sm
                               dark:border-ink-700"
                  />
                ) : (
                  <svg viewBox="0 0 24 24" className="h-7 w-7 text-ink-400" fill="none" stroke="currentColor"
                       strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
                    <circle cx="8.5" cy="10" r="1.6" />
                    <path d="M3.5 16.5l4.7-4.2a2 2 0 012.7 0l3.4 3.1a2 2 0 002.6.05l1.6-1.3 2 1.8" />
                  </svg>
                )}
                <span className="text-sm font-medium text-ink-800 dark:text-ink-100">
                  {file ? name : 'Drop a picture here'}
                </span>
                <span className="text-[12px] text-ink-500 dark:text-ink-400">
                  {file ? (
                    `${prettyBytes(file.size)} — click to choose a different one`
                  ) : (
                    <>
                      {/* The paste hint is for the desk. On a phone there is no
                          ⌘V to give, and the picker this opens offers the camera
                          — which is the thing to say to somebody standing in
                          front of the sign they cannot read. */}
                      <span className="sm:hidden">tap to choose a photo, or take one now</span>
                      <span className="hidden sm:inline">click to choose one, or paste with ⌘/Ctrl + V</span>
                    </>
                  )}
                </span>
              </label>

              {/* What we can actually take. Before the first upload, not after
                  it fails. */}
              {limits && (
                <ul className="mt-4 grid gap-1.5 text-[12px] text-ink-500 sm:grid-cols-2 dark:text-ink-400">
                  <li>· Up to {prettyBytes(limits.maxBytes)} per image</li>
                  <li>· {limits.formats.slice(0, 6).join(', ')}</li>
                  <li className="sm:col-span-2">
                    · {limits.perDay} images a day, free · the text fills the box, up to{' '}
                    {limits.maxChars.toLocaleString()} characters
                  </li>
                  <li className="sm:col-span-2">
                    · Photographs are read as they are — a sideways or slightly blurred one is fine
                  </li>
                </ul>
              )}

              {(busy || phase === 'done') && (
                <div className="mt-5">
                  <div className="flex items-baseline justify-between text-[12px] text-ink-500 dark:text-ink-400">
                    <span>
                      {phase === 'uploading' && 'Sending the picture…'}
                      {phase === 'reading' && 'Reading the writing in it…'}
                      {phase === 'done' && (text ? 'Done' : 'Nothing to read')}
                    </span>
                    <span className="tabular-nums">{pct}%</span>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-ink-200 dark:bg-ink-700">
                    <div
                      className="h-full rounded-full bg-accent-600 transition-[width] duration-300 dark:bg-accent-400"
                      style={{ width: `${Math.max(pct, 3)}%` }}
                    />
                  </div>

                  {/* The words, before they go anywhere. `whitespace-pre-wrap`
                      is the whole point: the server was asked to keep a menu's
                      dish and its price on one line, and a normal paragraph
                      block would throw that layout away on the last step. */}
                  {text && (
                    <div className="mt-4 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-xl bg-ink-100 p-3
                                    text-[13px] leading-relaxed text-ink-800 dark:bg-ink-800 dark:text-ink-100">
                      {text}
                    </div>
                  )}

                  {phase === 'done' && text && (
                    <p className="mt-2 text-[12px] text-ink-500 dark:text-ink-400">
                      {text.length.toLocaleString()} characters
                      {result?.width ? ` · read at ${result.width}×${result.height}` : ''}
                    </p>
                  )}

                  {/* The picture had nothing written on it. A fact about the
                      photo rather than a failure of the site, and it comes with
                      the one thing worth trying next. */}
                  {phase === 'done' && !text && result?.message && (
                    <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-700
                                  dark:bg-amber-500/10 dark:text-amber-400">
                      {result.message}
                    </p>
                  )}

                  {result?.truncated && (
                    <p className="mt-2 text-[12px] text-amber-600 dark:text-amber-400">
                      There was more writing than the box holds — the text stops at{' '}
                      {limits?.maxChars.toLocaleString()} characters.
                    </p>
                  )}
                </div>
              )}

              {error && (
                <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-600
                              dark:bg-red-500/10 dark:text-red-400">
                  {error}
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-ink-200 px-5 py-3
                        dark:border-ink-800">
          <button
            type="button"
            onClick={close}
            className="rounded-full px-4 py-2 text-[13px] font-medium text-ink-600 transition
                       hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800"
          >
            {busy ? 'Cancel' : 'Close'}
          </button>
          {phase === 'done' && text ? (
            <button
              type="button"
              onClick={() => { onText(text); onClose(); }}
              className="rounded-full bg-accent-600 px-4 py-2 text-[13px] font-medium text-white transition
                         hover:brightness-110 dark:bg-accent-400 dark:text-accent-700"
            >
              Translate it
            </button>
          ) : (
            <button
              type="button"
              onClick={parse}
              disabled={!file || busy}
              className="rounded-full bg-accent-600 px-4 py-2 text-[13px] font-medium text-white transition
                         enabled:hover:brightness-110 disabled:opacity-40 dark:bg-accent-400 dark:text-accent-700"
            >
              {busy ? 'Working…' : 'Parse'}
            </button>
          )}
        </div>
      </div>
    </div>
    </Layer>
  );
}
